import React from 'react'
import PropTypes from 'prop-types'
import { Button, Row, Image, Separator, Paragraph, H4, Grid, LayoutBand } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { imageErrorStates } from '../../utils/imageErrorStates'
import image from '../errorBoundaryFallback/images/oops.svg'

/**
 * Full-page message shown when a single image can't be displayed — it doesn't exist, the patron
 * can't see it, or the lookup failed. Stands in for the viewer, so it always offers a way out.
 *
 * This is the single-image counterpart to Lightyear's ImageGroupError, which covers image groups.
 *
 * @param {Object} props
 * @param {string} props.errorState - An `imageErrorStates` value.
 * @returns {JSX.Element}
 */
export default function ImageUnavailable({ errorState }) {
  const [t] = useTranslation()

  let heading
  let body
  if (errorState === imageErrorStates.NOT_FOUND) {
    heading = t('ImageUnavailable.notFoundHeader', 'Image Not Found')
    body = t(
      'ImageUnavailable.notFoundBody',
      "We can't find this image. The link may be out of date, or the image may no longer be part of this collection. Try searching for the record again."
    )
  } else if (errorState === imageErrorStates.UNAUTHORIZED) {
    heading = t('ImageUnavailable.unauthorizedHeader', 'Unable to Display Image')
    body = t(
      'ImageUnavailable.unauthorizedBody',
      "We don't have permission to share this image. Access is usually determined by the record's creator or custodian, or by data privacy laws."
    )
  } else {
    heading = t('ImageUnavailable.unavailableHeader', 'Cannot Display Image')
    body = t(
      'ImageUnavailable.unavailableBody',
      'Something went wrong on our end. Refreshing the page usually fixes it. If it keeps happening, try again in a little while.'
    )
  }

  // Refreshing only helps when the failure was transient; a missing or restricted image stays that way.
  const canRetry = errorState === imageErrorStates.UNAVAILABLE

  return (
    <LayoutBand maxWidth="xs" marginX="sm" minHeight="100%" alignY="middle" data-testid="image-unavailable">
      <Row alignX="center">
        <Image maxHeight={315} maxWidth="100%" src={image} alt="" aria-hidden="true" />
      </Row>
      <Separator size="md" />
      <Row alignX="center">
        <H4 centered>{heading}</H4>
      </Row>
      <Separator size="xxs" />
      <Row alignX="center">
        <Paragraph centered>{body}</Paragraph>
      </Row>
      <Separator size="md" />
      <Grid>
        <Row alignX="center">
          <Button data-testid="go-back-button" onClick={() => window.history.back()}>
            {t('tasks-done-overlay.go-back.button', 'Go Back')}
          </Button>
          {canRetry && (
            <Button data-testid="refresh-button" onClick={() => window.location.reload()} emphasis="high">
              {t('error-overlay.submit', 'Refresh')}
            </Button>
          )}
        </Row>
      </Grid>
    </LayoutBand>
  )
}

ImageUnavailable.propTypes = {
  errorState: PropTypes.oneOf(Object.values(imageErrorStates)).isRequired,
}
